import { easeExpInOut } from 'd3-ease';
// tslint:disable-next-line: no-submodule-imports
import useStyles from 'isomorphic-style-loader/useStyles';
import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import { isVisualPlayQueueDisabled, isTransportOverlayLoaded } from '../utils/TransportOverlayHook';
import { HIDE_TRANSPORT_OVERLAY, TRANSPORT_OVERLAY_ACTIVE_QUEUES, TRANSPORT_OVERLAY_PLAY_QUEUE } from '../actions';
import { VISUAL_TABLE_WIDGET } from '../types/templates/widgets/ISkyfireVisualTableWidget';
import { dispatchPlaybackMethods } from '../utils/dispatchPlaybackMethods';
import * as Styles from './TransportOverlay.scss';
import Widget from './widgets';
import VisualTableWidget from './widgets/VisualTableWidget';
const headerStyles = {
    contain: 'layout',
    paddingTop: '20px',
    display: 'block',
    marginBottom: '0px',
};
export default function TransportOverlay() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    useStyles(Styles);
    const dispatch = useDispatch();
    const { playQueue } = useSelector((state) => state.Media, shallowEqual);
    const { activeQueuesData } = useSelector((state) => state.Media, shallowEqual);
    const { id, isTransportOverlayOpen, onDataRequired } = useSelector((state) => state.TransportOverlay, shallowEqual);
    const overlayTemplate = id === TRANSPORT_OVERLAY_ACTIVE_QUEUES ? activeQueuesData : playQueue;
    const { windowHeight } = useSelector((state) => state.BrowserState, shallowEqual);
    const { mediaId } = useSelector((state) => state.Media);
    const playQueueDisabled = isVisualPlayQueueDisabled();
    const hasLoaded = isTransportOverlayLoaded();
    const emptyFunction = () => true;
    const hasNoUpcomingWidgets = ((_d = (_c = (_b = (_a = overlayTemplate) === null || _a === void 0 ? void 0 : _a.widgets) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.items) === null || _d === void 0 ? void 0 : _d.length) === 0;
    const trackCountClassName = !hasLoaded ? Styles.countLoading : '';
    const durationClassName = !hasLoaded ? Styles.durationLoading : '';
    const TransportOverlayAnimationValues = useSpring({
        opacity: isTransportOverlayOpen ? 1 : 0,
        height: isTransportOverlayOpen ? windowHeight - 80 : 0,
        config: { easing: easeExpInOut },
    });
    let timeoutHandle;
    // Check if Transport Overlay is open, disable root scroller to use Transport Overlay scroller.
    useEffect(() => {
        var _a;
        const windowScrollElement = document.documentElement;
        if (isTransportOverlayOpen) {
            (_a = timeoutHandle) === null || _a === void 0 ? void 0 : _a.clear();
            windowScrollElement.style['overflow-y'] = 'hidden';
        }
        else {
            timeoutHandle = setTimeout(() => {
                windowScrollElement.style['overflow-y'] = 'scroll';
            }, 500);
        }
    }, [isTransportOverlayOpen]);
    // If there's no data, fetch some. We need to fetch data, even if the Transport Overlay isn't open for
    // the time being to show the disabled state for the Transport Overlay Button until UX has a better
    // solution.
    useEffect(() => {
        if (!overlayTemplate) {
            dispatchPlaybackMethods(dispatch, mediaId, onDataRequired);
        }
    }, [mediaId, overlayTemplate]);
    // Handle Escape key shortcut.
    useEffect(() => {
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, []);
    // If there's no upcoming widgets, hide the Transport Overlay.
    useEffect(() => {
        if (hasNoUpcomingWidgets && hasLoaded) {
            dispatch({ type: HIDE_TRANSPORT_OVERLAY });
        }
    }, [hasNoUpcomingWidgets]);
    useEffect(() => {
        if (isTransportOverlayOpen && playQueueDisabled && id === TRANSPORT_OVERLAY_PLAY_QUEUE) {
            dispatch({ type: HIDE_TRANSPORT_OVERLAY });
        }
    }, [playQueueDisabled]);
    const widgets = (_e = overlayTemplate) === null || _e === void 0 ? void 0 : _e.widgets;
    return (React.createElement(animated.div, { style: TransportOverlayAnimationValues, className: Styles.transportOverlay, id: "transport-overlay" },
        React.createElement("music-detail-header", { style: headerStyles, headline: (_f = overlayTemplate) === null || _f === void 0 ? void 0 : _f.title, className: hasLoaded ? '' : Styles.hidden }),
        React.createElement("header", { className: hasLoaded ? Styles.hidden : Styles.headerLoading }),
        React.createElement("b", { className: Styles.subtitle },
            ((_g = overlayTemplate) === null || _g === void 0 ? void 0 : _g.subTitle) && (React.createElement("span", { className: Styles.subtitleText }, (_h = overlayTemplate) === null || _h === void 0 ? void 0 : _h.subTitle)),
            ((_j = overlayTemplate) === null || _j === void 0 ? void 0 : _j.trackCount) && (React.createElement("span", { className: trackCountClassName }, (_k = overlayTemplate) === null || _k === void 0 ? void 0 : _k.trackCount)),
            isTransportOverlayOpen && ((_l = overlayTemplate) === null || _l === void 0 ? void 0 : _l.totalDuration) ? React.createElement("span", null, "\u2022") : null,
            isTransportOverlayOpen && (React.createElement("span", { className: durationClassName }, convertToReadableTime((_m = overlayTemplate) === null || _m === void 0 ? void 0 : _m.totalDuration)))),
        React.createElement("div", { className: hasLoaded ? '' : Styles.hidden }, renderWidgets(widgets)),
        React.createElement("div", { className: hasLoaded ? Styles.hidden : '' },
            React.createElement(VisualTableWidget, { data: {
                    interface: VISUAL_TABLE_WIDGET,
                    items: Array(20).fill(1),
                    onEndOfWidget: [],
                    onTrackReorder: [],
                    onViewed: [],
                }, handleSelected: emptyFunction, loading: true }))));
    function handleEscape(e) {
        if (e.keyCode === 27) {
            dispatch({ type: HIDE_TRANSPORT_OVERLAY });
        }
    }
    function convertToReadableTime(durationString) {
        if (!durationString) {
            return undefined;
        }
        const minutes = Number(durationString.substr(0, durationString.indexOf(':')));
        if (minutes) {
            return String(Math.floor(minutes / 60)) + 'hr ' + String(minutes % 60) + ' min';
        }
        return undefined;
    }
    function renderWidgets(widgetsToRender) {
        var _a;
        return (_a = widgetsToRender) === null || _a === void 0 ? void 0 : _a.map((widget) => {
            if (widget.interface === VISUAL_TABLE_WIDGET) {
                return (React.createElement(VisualTableWidget, { data: widget, handleSelected: handleSelected, isVisualPlayQueue: true }));
            }
            return React.createElement(Widget, { data: widget, handleSelected: handleSelected });
        });
    }
    function handleSelected(onItemSelected) {
        var _a;
        if (!((_a = onItemSelected) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        dispatchPlaybackMethods(dispatch, mediaId, onItemSelected);
        dispatch({ type: HIDE_TRANSPORT_OVERLAY });
        dispatchPlaybackMethods(dispatch, mediaId, onDataRequired);
    }
}
